import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AllEInput } from "../../../functions/_lib/alle-check/types";
import { calculateAllEEligibility } from "../../../functions/_lib/alle-check/calculator";
import {
  buildRecommenderInput,
  combineRecommenderVerdict,
  type RecommenderFormState,
} from "./recommender-input";
import { useLang, useT } from "../../i18n/language";

/**
 * Step-by-step (configurator) renderer for the All-e recommender.
 *
 * Mirrors the long-form CheckForm field set, but shows one question card at a
 * time with progress bar, back / next CTAs, and a sticky bottom bar on phones.
 * Reuses RecommenderFormState + buildRecommenderInput so the engine call shape
 * is identical to the long-form view.
 */

interface Props {
  onSubmit: (input: AllEInput) => void;
  isSubmitting: boolean;
  onStateChange?: (state: RecommenderFormState) => void;
}

const HEATING_OPTIONS = [
  { v: "Radiatoren", l: "Radiatoren" },
  { v: "Vloerverwarming", l: "Vloerverwarming" },
  { v: "Vloerverwarming + andere bronnen", l: "Vloerverwarming + andere bronnen" },
  { v: "Vloerconvectoren", l: "Vloerconvectoren" },
  { v: "Luchtverwarming", l: "Luchtverwarming" },
  { v: "Stadsverwarming", l: "Stadsverwarming" },
];

const HOUSE_TYPES = ["Vrijstaand", "2-onder-1-kap", "Tussenwoning", "Hoekwoning", "Appartement"];
const HOUSE_TYPE_SUBS: Record<string, string> = {
  "Vrijstaand": "Aan alle kanten vrij",
  "2-onder-1-kap": "Aan één zijde geschakeld",
  "Tussenwoning": "Tussen twee buren in",
  "Hoekwoning": "Op de hoek van de rij",
  "Appartement": "Etagewoning of flat",
};
const ENERGY_LABELS = ["", "A++++", "A+++", "A++", "A+", "A", "B", "C", "D", "E", "F", "G"];

const BUILD_YEARS = [
  { v: "voor_1970", l: "Voor 1970" },
  { v: "1970_1990", l: "1970-1990" },
  { v: "1990_2005", l: "1990-2005" },
  { v: "na_2005", l: "Na 2005" },
];

const INSULATION_OPTIONS = [
  { v: "spouwmuur", l: "Spouwmuurisolatie" },
  { v: "dak", l: "Dakisolatie" },
  { v: "vloer", l: "Vloerisolatie" },
  { v: "hr_glas", l: "HR++ of triple glas" },
];

const DISTRIBUTION_OPTIONS = [
  { v: "Radiatoren", l: "Radiatoren" },
  { v: "Vloerverwarming", l: "Vloerverwarming" },
  { v: "Vloerverwarming + andere bronnen", l: "Vloerverwarming + andere bronnen" },
];

const RADIATOR_TYPES: Array<{ v: "10" | "11" | "20" | "21" | "22" | "33"; l: string }> = [
  { v: "10", l: "Type 10" },
  { v: "11", l: "Type 11" },
  { v: "20", l: "Type 20" },
  { v: "21", l: "Type 21" },
  { v: "22", l: "Type 22" },
  { v: "33", l: "Type 33" },
];

const RAD_W_M2_UI: Record<string, number> = {
  "10": 270,
  "11": 460,
  "20": 536,
  "21": 663,
  "22": 918,
  "33": 1326,
};

function defaultState(): RecommenderFormState {
  return {
    house_type: "",
    apartment_vve: null,
    apartment_outdoor_space: null,
    heating_types: [],
    is_renovating: false,
    route: "gas",
    yearly_gas_m3: "1200",
    summer_avg_gas_m3: "20",
    beng1_value: "",
    floor_area_m2: "",
    build_year: "1990_2005",
    energy_label: "",
    insulation_measures: [],
    cv_comfort_55c: true,
    silent_mode_at_night: false,
    distribution_main_room: "Radiatoren",
    distribution_whole_house: "Radiatoren",
    day_temp: "20",
    night_temp: "19",
    resident_number: "",
    boiler_age: "",
    hot_water_usage: "",
    estimated_electricity_usage: "",
    insulation_score_roof: "",
    insulation_score_wall: "",
    insulation_score_floor: "",
    insulation_score_windows: "",
    postal_code: "",
    house_number: "",
    house_number_addition: "",
    address_street: "",
    address_city: "",
    cv_temp_c: "",
    manifold_pump: null,
    radiators: [
      { type: "", length_cm: "", height_cm: "", room: "" },
      { type: "", length_cm: "", height_cm: "", room: "" },
      { type: "", length_cm: "", height_cm: "", room: "" },
    ],
    radiator_coverage_pct: "70",
    zone_thermostat: "",
    extra_heat_sources: "",
    cw_class: "",
  };
}

const OptionCheckBadge = () => (
  <span className="option-card-check" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  </span>
);

const ThermometerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 4a2 2 0 0 0-4 0v10.54a4 4 0 1 0 4 0z" />
  </svg>
);

// Compact woning-iconen voor de option cards. Niet 100% getrouw aan
// architectuur, maar visueel onderscheidend genoeg om de cards te lezen.
const HouseTypeIcon = ({ type }: { type: string }) => {
  const common = {
    width: 28,
    height: 28,
    viewBox: "0 0 32 32",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (type) {
    case "Vrijstaand":
      return (
        <svg {...common}>
          <path d="M5 15l11-9 11 9" />
          <path d="M8 14v12h16V14" />
          <rect x="14" y="18" width="4" height="8" />
        </svg>
      );
    case "2-onder-1-kap":
      return (
        <svg {...common}>
          <path d="M2 16l7-7 7 7" />
          <path d="M16 16l7-7 7 7" />
          <path d="M4 15v11h12V15" />
          <path d="M18 15v11h12V15" />
          <rect x="9" y="20" width="3" height="6" />
          <rect x="22" y="20" width="3" height="6" />
        </svg>
      );
    case "Tussenwoning":
      return (
        <svg {...common}>
          <path d="M2 14v12h28V14" />
          <path d="M6 14l10-7 10 7" />
          <rect x="14" y="18" width="4" height="8" />
        </svg>
      );
    case "Hoekwoning":
      return (
        <svg {...common}>
          <path d="M3 26V14l9-7 9 7" />
          <path d="M21 14h7v12" />
          <path d="M3 26h25" />
          <rect x="10" y="18" width="4" height="8" />
        </svg>
      );
    case "Appartement":
      return (
        <svg {...common}>
          <rect x="6" y="4" width="20" height="24" />
          <path d="M11 9h3M18 9h3M11 14h3M18 14h3M11 19h3M18 19h3" />
          <rect x="14" y="23" width="4" height="5" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M5 15l11-9 11 9" />
          <path d="M8 14v12h16V14" />
        </svg>
      );
  }
};

const HeatingTypeIcon = ({ type }: { type: string }) => {
  const c = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (type.startsWith("Vloer")) {
    return (
      <svg {...c}>
        <path d="M3 8h18M3 13h18M3 18h18" />
      </svg>
    );
  }
  if (type === "Luchtverwarming") {
    return (
      <svg {...c}>
        <path d="M3 12h14a3 3 0 1 0-3-3" />
        <path d="M3 17h10a3 3 0 1 1-3 3" />
      </svg>
    );
  }
  if (type === "Stadsverwarming") {
    return (
      <svg {...c}>
        <path d="M6 21V11l6-5 6 5v10" />
        <path d="M10 21v-5h4v5" />
      </svg>
    );
  }
  // Radiatoren / vloerconvectoren default
  return (
    <svg {...c}>
      <rect x="4" y="6" width="16" height="12" rx="1.5" />
      <path d="M8 6v12M12 6v12M16 6v12" />
    </svg>
  );
};

interface StepDef {
  id: string;
  title: string;
  subtitle?: string;
}

export function CheckFormStepper({ onSubmit, isSubmitting, onStateChange }: Props) {
  const t = useT();
  const { lang } = useLang();
  const [s, setS] = useState<RecommenderFormState>(defaultState);
  const [stepIdx, setStepIdx] = useState(0);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const stepCardRef = useRef<HTMLDivElement | null>(null);

  const update = useCallback(<K extends keyof RecommenderFormState>(k: K, v: RecommenderFormState[K]) => {
    setS((prev) => ({ ...prev, [k]: v }));
  }, []);

  const toggleArrayValue = useCallback(
    (key: "heating_types" | "insulation_measures", value: string) => {
      setS((prev) => {
        const has = prev[key].includes(value);
        return { ...prev, [key]: has ? prev[key].filter((x) => x !== value) : [...prev[key], value] };
      });
    },
    [],
  );

  useEffect(() => {
    onStateChange?.(s);
  }, [s, onStateChange]);

  const isApartment = s.house_type === "Appartement";
  const buildInput = useMemo<AllEInput>(() => buildRecommenderInput(s), [s]);

  const cvTempNum = Number(s.cv_temp_c);
  const cvIsHot = !s.cv_comfort_55c || (Number.isFinite(cvTempNum) && cvTempNum > 60);
  const isRadiatorSystem = s.distribution_whole_house === "Radiatoren";
  const showRadiatorStep = cvIsHot && isRadiatorSystem;
  const isFloorHeating =
    s.distribution_whole_house === "Vloerverwarming" ||
    s.distribution_whole_house === "Vloerverwarming + andere bronnen";

  // Build the dynamic step list. Conditional steps drop in/out without
  // renumbering the badge (Stap X van N reflects the active set).
  const steps: StepDef[] = useMemo(() => {
    const arr: StepDef[] = [
      { id: "house_type", title: t("Wat voor woning is het?") },
      { id: "heating_types", title: t("Hoe wordt de woning nu verwarmd?"), subtitle: t("Meerdere antwoorden mogelijk.") },
      { id: "route", title: t("Welke gegevens heb je bij de hand?"), subtitle: t("Kies de input die je het makkelijkst kunt invullen.") },
      { id: "insulation", title: t("Hoe goed is de woning geïsoleerd?") },
      { id: "cv_temp", title: t("Hoe heet stookt de CV-ketel nu?") },
      { id: "distribution", title: t("Hoe wordt de warmte verdeeld?") },
      { id: "temps", title: t("Welke binnentemperaturen wil je halen?") },
      { id: "thermostat", title: t("Een paar laatste vragen") },
    ];
    if (showRadiatorStep) {
      arr.push({
        id: "radiators",
        title: t("Radiatorcheck"),
        subtitle: t("Bij hoge CV-aanvoer kijken we of de radiatoren ook op W55 voldoende zijn."),
      });
    }
    arr.push({
      id: "address",
      title: t("Adres (optioneel)"),
      subtitle: t("Vul postcode + huisnummer in om straat en plaats automatisch op te halen."),
    });
    arr.push({ id: "review", title: t("Klaar voor je aanbeveling") });
    return arr;
  }, [showRadiatorStep, t]);

  const totalSteps = steps.length;
  const safeIdx = Math.min(stepIdx, totalSteps - 1);
  const current = steps[safeIdx] ?? steps[0]!;
  const progressPct = ((safeIdx + 1) / totalSteps) * 100;

  // Live verdict preview (mobile-friendly compact result strip).
  const livePreview = useMemo(() => {
    try {
      const out = calculateAllEEligibility(buildInput, lang);
      const verdict = combineRecommenderVerdict(out);
      const cap = out.check1_heat_demand.coverage_pct;
      const pump = out.check1_heat_demand.recommended_pump;
      return { verdict, cap, pump };
    } catch {
      return null;
    }
  }, [buildInput, lang]);

  // Auto-scroll the active step card into view (sticky-header offset).
  useEffect(() => {
    if (!stepCardRef.current) return;
    const top = stepCardRef.current.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top, behavior: "smooth" });
  }, [safeIdx]);

  const goNext = () => {
    if (safeIdx < totalSteps - 1) setStepIdx(safeIdx + 1);
  };
  const goBack = () => {
    if (safeIdx > 0) setStepIdx(safeIdx - 1);
  };

  const lookupAddress = async () => {
    setAddressLookupError(null);
    const normalizedPostcode = s.postal_code.replace(/\s+/g, "").toUpperCase();
    const normalizedHouse = s.house_number.trim();
    if (!normalizedPostcode || !normalizedHouse) {
      setAddressLookupError(t("Vul postcode en huisnummer in."));
      return;
    }
    if (!/^\d{4}[A-Z]{2}$/.test(normalizedPostcode)) {
      setAddressLookupError(t("Ongeldige postcode (verwacht: 1234AB)."));
      return;
    }
    if (normalizedPostcode !== s.postal_code) update("postal_code", normalizedPostcode);
    setAddressLookupLoading(true);
    try {
      const res = await fetch(
        `/api/v1/lookup/address?postal_code=${encodeURIComponent(normalizedPostcode)}&house_number=${encodeURIComponent(normalizedHouse)}`,
      );
      const text = await res.text();
      let body: {
        street?: string;
        city?: string;
        error?: string;
        floor_area_m2?: number | null;
        energy_label?: string | null;
      } = {};
      try {
        body = text ? (JSON.parse(text) as typeof body) : {};
      } catch {
        setAddressLookupError(
          res.ok
            ? t("Onverwacht antwoord van de server.")
            : `${t("Adres-lookup is op deze omgeving niet beschikbaar")} (HTTP ${res.status}).`,
        );
        return;
      }
      if (!res.ok) {
        setAddressLookupError(body.error ?? `${t("Adres-lookup faalde")} (HTTP ${res.status}).`);
        return;
      }
      update("address_street", body.street ?? "");
      update("address_city", body.city ?? "");
      // Auto-fill the model-driving inputs from BAG / EP-online so the installer
      // does not have to type them. Both stay editable.
      if (typeof body.floor_area_m2 === "number" && body.floor_area_m2 > 0) {
        update("floor_area_m2", String(body.floor_area_m2));
      }
      if (body.energy_label) {
        update("energy_label", body.energy_label);
      }
    } catch (e) {
      setAddressLookupError(e instanceof Error ? e.message : t("Onbekende fout."));
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const addRadiator = () => {
    setS((prev) => ({
      ...prev,
      radiators: [...prev.radiators, { type: "", length_cm: "", height_cm: "", room: "" }],
    }));
  };
  const removeRadiator = (idx: number) => {
    setS((prev) => ({
      ...prev,
      radiators: prev.radiators.filter((_, i) => i !== idx),
    }));
  };
  const updateRadiator = (
    idx: number,
    patch: Partial<RecommenderFormState["radiators"][number]>,
  ) => {
    setS((prev) => ({
      ...prev,
      radiators: prev.radiators.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const renderStep = () => {
    switch (current.id) {
      case "house_type":
        return (
          <div className="stepper-body">
            <div className="option-card-grid option-card-grid-houses">
              {HOUSE_TYPES.map((ht, i) => {
                const selected = s.house_type === ht;
                return (
                  <button
                    key={ht}
                    type="button"
                    className={`option-card ${selected ? "selected" : ""}`}
                    style={{ animationDelay: `${i * 70}ms` }}
                    onClick={() => update("house_type", ht)}
                  >
                    {selected && <OptionCheckBadge />}
                    <span className="option-card-icon"><HouseTypeIcon type={ht} /></span>
                    <span className="option-card-label">{t(ht)}</span>
                    <span className="option-card-sub">{HOUSE_TYPE_SUBS[ht] ? t(HOUSE_TYPE_SUBS[ht]!) : ""}</span>
                  </button>
                );
              })}
            </div>
            {isApartment && (
              <>
                <div className="stepper-subq">
                  <label className="question-label">{t("VVE-toestemming verkregen?")}</label>
                  <div className="option-card-grid option-card-grid-yesno">
                    <button type="button" className={`option-card option-card-compact ${s.apartment_vve === true ? "selected" : ""}`} onClick={() => update("apartment_vve", true)}>
                      {s.apartment_vve === true && <OptionCheckBadge />}
                      <span className="option-card-label">{t("Ja")}</span>
                    </button>
                    <button type="button" className={`option-card option-card-compact ${s.apartment_vve === false ? "selected" : ""}`} onClick={() => update("apartment_vve", false)}>
                      {s.apartment_vve === false && <OptionCheckBadge />}
                      <span className="option-card-label">{t("Nee")}</span>
                    </button>
                  </div>
                </div>
                <div className="stepper-subq">
                  <label className="question-label">{t("Buitenruimte beschikbaar voor de buitenunit?")}</label>
                  <div className="option-card-grid option-card-grid-yesno">
                    <button type="button" className={`option-card option-card-compact ${s.apartment_outdoor_space === true ? "selected" : ""}`} onClick={() => update("apartment_outdoor_space", true)}>
                      {s.apartment_outdoor_space === true && <OptionCheckBadge />}
                      <span className="option-card-label">{t("Ja")}</span>
                    </button>
                    <button type="button" className={`option-card option-card-compact ${s.apartment_outdoor_space === false ? "selected" : ""}`} onClick={() => update("apartment_outdoor_space", false)}>
                      {s.apartment_outdoor_space === false && <OptionCheckBadge />}
                      <span className="option-card-label">{t("Nee")}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case "heating_types":
        return (
          <div className="stepper-body">
            <div className="option-card-grid option-card-grid-multi">
              {HEATING_OPTIONS.map((h, i) => {
                const checked = s.heating_types.includes(h.v);
                return (
                  <button
                    key={h.v}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    className={`option-card option-card-multi ${checked ? "selected" : ""}`}
                    style={{ animationDelay: `${i * 60}ms` }}
                    onClick={() => toggleArrayValue("heating_types", h.v)}
                  >
                    {checked && <OptionCheckBadge />}
                    <span className="option-card-icon"><HeatingTypeIcon type={h.v} /></span>
                    <span className="option-card-label">{t(h.l)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "route":
        return (
          <div className="stepper-body">
            <div className="radio-cards">
              <button
                type="button"
                className={`radio-card ${s.route === "gas" ? "selected" : ""}`}
                onClick={() => update("route", "gas")}
              >
                <svg className="radio-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="radio-card-label">{t("Gasverbruik")}</div>
                <div className="radio-card-sub">{t("op basis van gasrekening")}</div>
              </button>
              <button
                type="button"
                className={`radio-card ${s.route === "beng1" ? "selected" : ""}`}
                onClick={() => update("route", "beng1")}
              >
                <svg className="radio-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <div className="radio-card-label">BENG1 / EP1</div>
                <div className="radio-card-sub">{t("op basis van energieprestatie")}</div>
              </button>
            </div>

            {s.route === "gas" ? (
              <div className="input-row stepper-input-row">
                <div className="input-group">
                  <label className="input-label">{t("Jaarlijks gasverbruik (m³)")}</label>
                  <input
                    className="text-input"
                    type="number"
                    inputMode="numeric"
                    value={s.yearly_gas_m3}
                    onChange={(e) => update("yearly_gas_m3", e.target.value)}
                    placeholder="1500"
                  />
                  <div className="helper-text">{t("Gemiddelde over de laatste 2 tot 3 jaar.")}</div>
                </div>
                <div className="input-group">
                  <label className="input-label">{t("Zomergemiddelde (m³/maand)")}</label>
                  <input
                    className="text-input"
                    type="number"
                    inputMode="numeric"
                    value={s.summer_avg_gas_m3}
                    onChange={(e) => update("summer_avg_gas_m3", e.target.value)}
                    placeholder="50"
                  />
                  <div className="helper-text">{t("Gemiddelde van juni, juli, augustus (warm water).")}</div>
                </div>
              </div>
            ) : (
              <div className="input-row stepper-input-row">
                <div className="input-group">
                  <label className="input-label">BENG1 / EP1 (kWh/m²)</label>
                  <input
                    className="text-input"
                    type="number"
                    inputMode="numeric"
                    value={s.beng1_value}
                    onChange={(e) => update("beng1_value", e.target.value)}
                    placeholder="125"
                  />
                  <div className="helper-text">{t("Warmtebehoefte uit ep-online.nl.")}</div>
                </div>
                <div className="input-group">
                  <label className="input-label">{t("Gebruiksoppervlakte (m²)")}</label>
                  <input
                    className="text-input"
                    type="number"
                    inputMode="numeric"
                    value={s.floor_area_m2}
                    onChange={(e) => update("floor_area_m2", e.target.value)}
                    placeholder="135"
                  />
                  <div className="helper-text">{t("Bruto vloeroppervlak.")}</div>
                </div>
              </div>
            )}
          </div>
        );

      case "insulation":
        return (
          <div className="stepper-body">
            <div className="stepper-subq">
              <label className="question-label">{t("Bouwjaar")}</label>
              <div className="option-card-grid option-card-grid-row">
                {BUILD_YEARS.map((b, i) => {
                  const selected = s.build_year === b.v;
                  return (
                    <button
                      key={b.v}
                      type="button"
                      className={`option-card option-card-compact ${selected ? "selected" : ""}`}
                      style={{ animationDelay: `${i * 50}ms` }}
                      onClick={() => update("build_year", b.v)}
                    >
                      {selected && <OptionCheckBadge />}
                      <span className="option-card-label">{t(b.l)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="stepper-subq">
              <label className="question-label">{t("Energielabel (indien bekend)")}</label>
              <select
                className="select-input"
                value={s.energy_label}
                onChange={(e) => update("energy_label", e.target.value)}
              >
                {ENERGY_LABELS.map((l) => (
                  <option key={l} value={l}>{l || t("Niet bekend")}</option>
                ))}
              </select>
            </div>
            <div className="stepper-subq">
              <label className="question-label">{t("Welke isolatie is al uitgevoerd?")}</label>
              <div className="option-card-grid option-card-grid-multi">
                {INSULATION_OPTIONS.map((m, i) => {
                  const checked = s.insulation_measures.includes(m.v);
                  return (
                    <button
                      key={m.v}
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      className={`option-card option-card-multi ${checked ? "selected" : ""}`}
                      style={{ animationDelay: `${i * 60}ms` }}
                      onClick={() => toggleArrayValue("insulation_measures", m.v)}
                    >
                      {checked && <OptionCheckBadge />}
                      <span className="option-card-label">{t(m.l)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case "cv_temp":
        return (
          <div className="stepper-body">
            <div className="stepper-subq">
              <label className="question-label">{t("Is de woning comfortabel op 55°C aanvoer?")}</label>
              <div className="option-card-grid option-card-grid-yesno">
                <button type="button" className={`option-card option-card-compact ${s.cv_comfort_55c ? "selected" : ""}`} onClick={() => update("cv_comfort_55c", true)}>
                  {s.cv_comfort_55c && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Ja")}</span>
                </button>
                <button type="button" className={`option-card option-card-compact ${!s.cv_comfort_55c ? "selected" : ""}`} onClick={() => update("cv_comfort_55c", false)}>
                  {!s.cv_comfort_55c && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Nee")}</span>
                </button>
              </div>
              <div className="helper-text">{t("Belangrijk voor All-Electric. Bij Nee gaan we extra controleren.")}</div>
            </div>
            <div className="stepper-subq">
              <label className="input-label">{t("Huidige CV-aanvoer (°C, optioneel)")}</label>
              <input
                className="text-input"
                type="number"
                inputMode="numeric"
                min="20"
                max="90"
                value={s.cv_temp_c}
                onChange={(e) => update("cv_temp_c", e.target.value)}
                placeholder="65"
              />
              <div className="helper-text">{t("Te vinden op het display van de CV-ketel.")}</div>
            </div>
            <div className="stepper-subq">
              <label className="question-label">{t("Stille modus 's nachts gewenst?")}</label>
              <div className="option-card-grid option-card-grid-yesno">
                <button type="button" className={`option-card option-card-compact ${s.silent_mode_at_night ? "selected" : ""}`} onClick={() => update("silent_mode_at_night", true)}>
                  {s.silent_mode_at_night && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Ja")}</span>
                </button>
                <button type="button" className={`option-card option-card-compact ${!s.silent_mode_at_night ? "selected" : ""}`} onClick={() => update("silent_mode_at_night", false)}>
                  {!s.silent_mode_at_night && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Nee")}</span>
                </button>
              </div>
            </div>
          </div>
        );

      case "distribution":
        return (
          <div className="stepper-body">
            <div className="stepper-subq">
              <label className="input-label">{t("Verwarmingssysteem hele woning")}</label>
              <select
                className="select-input"
                value={s.distribution_whole_house}
                onChange={(e) => update("distribution_whole_house", e.target.value)}
              >
                {DISTRIBUTION_OPTIONS.map((d) => (
                  <option key={d.v} value={d.v}>{t(d.l)}</option>
                ))}
              </select>
            </div>
            <div className="stepper-subq">
              <label className="input-label">{t("Hoofdruimte (woonkamer) distributie")}</label>
              <select
                className="select-input"
                value={s.distribution_main_room}
                onChange={(e) => update("distribution_main_room", e.target.value)}
              >
                {DISTRIBUTION_OPTIONS.map((d) => (
                  <option key={d.v} value={d.v}>{t(d.l)}</option>
                ))}
              </select>
            </div>
            {isFloorHeating && (
              <div className="stepper-subq">
                <label className="question-label">{t("Verdeler met pomp aanwezig?")}</label>
                <div className="option-card-grid option-card-grid-yesno">
                  <button
                    type="button"
                    className={`option-card option-card-compact ${s.manifold_pump === true ? "selected" : ""}`}
                    onClick={() => update("manifold_pump", true)}
                  >
                    {s.manifold_pump === true && <OptionCheckBadge />}
                    <span className="option-card-label">{t("Ja")}</span>
                  </button>
                  <button
                    type="button"
                    className={`option-card option-card-compact ${s.manifold_pump === false ? "selected" : ""}`}
                    onClick={() => update("manifold_pump", false)}
                  >
                    {s.manifold_pump === false && <OptionCheckBadge />}
                    <span className="option-card-label">{t("Nee")}</span>
                  </button>
                </div>
                <div className="helper-text">{t("Met verdeler-pomp rekenen we met W45, zonder pomp met W35.")}</div>
              </div>
            )}
          </div>
        );

      case "temps":
        return (
          <div className="stepper-body">
            <div className="input-row stepper-input-row">
              <div className="input-group">
                <label className="input-label">
                  <span className="input-label-icon"><ThermometerIcon /></span>
                  {t("Dagtemperatuur (°C)")}
                </label>
                <input
                  className="text-input"
                  type="number"
                  inputMode="numeric"
                  value={s.day_temp}
                  onChange={(e) => update("day_temp", e.target.value)}
                  placeholder="20"
                />
                <div className="helper-text">{t("Aanbevolen: 20°C overdag.")}</div>
              </div>
              <div className="input-group">
                <label className="input-label">
                  <span className="input-label-icon"><ThermometerIcon /></span>
                  {t("Nachttemperatuur (°C)")}
                </label>
                <input
                  className="text-input"
                  type="number"
                  inputMode="numeric"
                  value={s.night_temp}
                  onChange={(e) => update("night_temp", e.target.value)}
                  placeholder="19"
                />
                <div className="helper-text">{t("Aanbevolen: 18-19°C 's nachts.")}</div>
              </div>
            </div>
            <div className="helper-text">{t("Kleinere setback (≤ 4°C verschil) is gunstiger voor een warmtepomp.")}</div>
          </div>
        );

      case "thermostat":
        return (
          <div className="stepper-body">
            <div className="stepper-subq">
              <label className="question-label">{t("Zone-thermostaat aanwezig?")}</label>
              <div className="option-card-grid option-card-grid-yesno">
                <button
                  type="button"
                  className={`option-card option-card-compact ${s.zone_thermostat === "ja" ? "selected" : ""}`}
                  onClick={() => update("zone_thermostat", "ja")}
                >
                  {s.zone_thermostat === "ja" && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Ja")}</span>
                </button>
                <button
                  type="button"
                  className={`option-card option-card-compact ${s.zone_thermostat === "nee" ? "selected" : ""}`}
                  onClick={() => update("zone_thermostat", "nee")}
                >
                  {s.zone_thermostat === "nee" && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Nee")}</span>
                </button>
              </div>
              <div className="helper-text">{t("Per-kamer thermostaten (Tado, Evohome, Anna) passen niet bij de Quatt warmtepomp.")}</div>
            </div>
            <div className="stepper-subq">
              <label className="question-label">{t("Extra warmtebronnen (haard, airco, IR-paneel)?")}</label>
              <div className="option-card-grid option-card-grid-yesno">
                <button
                  type="button"
                  className={`option-card option-card-compact ${s.extra_heat_sources === "ja" ? "selected" : ""}`}
                  onClick={() => update("extra_heat_sources", "ja")}
                >
                  {s.extra_heat_sources === "ja" && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Ja")}</span>
                </button>
                <button
                  type="button"
                  className={`option-card option-card-compact ${s.extra_heat_sources === "nee" ? "selected" : ""}`}
                  onClick={() => update("extra_heat_sources", "nee")}
                >
                  {s.extra_heat_sources === "nee" && <OptionCheckBadge />}
                  <span className="option-card-label">{t("Nee")}</span>
                </button>
              </div>
            </div>
            <div className="stepper-subq">
              <label className="input-label">{t("Tapwaterklasse (CW)")}</label>
              <select
                className="select-input"
                value={s.cw_class}
                onChange={(e) => update("cw_class", e.target.value as RecommenderFormState["cw_class"])}
              >
                <option value="">{t("Onbekend")}</option>
                <option value="CW5">CW5</option>
                <option value="CW6">CW6</option>
              </select>
            </div>
          </div>
        );

      case "radiators":
        return (
          <div className="stepper-body">
            <p className="card-subtitle">
              {t("Vul de grootste radiatoren in. Klein of zelden gebruikte ruimtes mag je overslaan.")}
            </p>
            <div className="stepper-radiators">
              {s.radiators.map((r, idx) => {
                const wm2 = r.type ? RAD_W_M2_UI[r.type] ?? 0 : 0;
                const kw =
                  (wm2 * ((Number(r.length_cm) || 0) / 100) * ((Number(r.height_cm) || 0) / 100)) / 1000;
                return (
                  <div key={idx} className="stepper-radiator-row">
                    <div className="stepper-radiator-head">
                      <span className="stepper-radiator-idx">{t("Radiator")} {idx + 1}</span>
                      {s.radiators.length > 1 && (
                        <button
                          type="button"
                          className="stepper-radiator-remove"
                          onClick={() => removeRadiator(idx)}
                          aria-label={`${t("Radiator")} ${idx + 1} ${t("verwijderen")}`}
                        >
                          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="input-row stepper-input-row">
                      <div className="input-group">
                        <label className="input-label">{t("Ruimte")}</label>
                        <input
                          className="text-input"
                          type="text"
                          value={r.room}
                          onChange={(e) => updateRadiator(idx, { room: e.target.value })}
                          placeholder={t("Woonkamer")}
                        />
                      </div>
                      <div className="input-group">
                        <label className="input-label">Type</label>
                        <select
                          className="select-input"
                          value={r.type}
                          onChange={(e) =>
                            updateRadiator(idx, {
                              type: e.target.value as RecommenderFormState["radiators"][number]["type"],
                            })
                          }
                        >
                          <option value="">{t("Kies type")}</option>
                          {RADIATOR_TYPES.map((rt) => (
                            <option key={rt.v} value={rt.v}>{rt.l}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="input-row stepper-input-row">
                      <div className="input-group">
                        <label className="input-label">{t("Lengte (cm)")}</label>
                        <input
                          className="text-input"
                          type="number"
                          inputMode="numeric"
                          value={r.length_cm}
                          onChange={(e) => updateRadiator(idx, { length_cm: e.target.value })}
                          placeholder="120"
                        />
                      </div>
                      <div className="input-group">
                        <label className="input-label">{t("Hoogte (cm)")}</label>
                        <input
                          className="text-input"
                          type="number"
                          inputMode="numeric"
                          value={r.height_cm}
                          onChange={(e) => updateRadiator(idx, { height_cm: e.target.value })}
                          placeholder="60"
                        />
                      </div>
                    </div>
                    <div className="stepper-radiator-power">
                      {t("Vermogen:")} <strong>{kw > 0 ? `${kw.toFixed(2)} kW` : "-"}</strong> @ W55
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={addRadiator}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8 }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14M5 12h14" />
              </svg>
              {t("Radiator toevoegen")}
            </button>
            <div className="stepper-subq">
              <label className="input-label">{t("Ingevuld aandeel van totale warmtevraag (%)")}</label>
              <input
                className="text-input"
                type="number"
                inputMode="numeric"
                min="1"
                max="100"
                value={s.radiator_coverage_pct}
                onChange={(e) => update("radiator_coverage_pct", e.target.value)}
                placeholder="70"
              />
            </div>
          </div>
        );

      case "address":
        return (
          <div className="stepper-body">
            <div className="input-row cols-3 stepper-input-row">
              <div className="input-group">
                <label className="input-label">{t("Postcode")}</label>
                <input
                  className="text-input"
                  type="text"
                  value={s.postal_code}
                  onChange={(e) => update("postal_code", e.target.value.toUpperCase())}
                  placeholder="1062HJ"
                />
              </div>
              <div className="input-group">
                <label className="input-label">{t("Huisnummer")}</label>
                <input
                  className="text-input"
                  type="text"
                  inputMode="numeric"
                  value={s.house_number}
                  onChange={(e) => update("house_number", e.target.value)}
                  placeholder="29"
                />
              </div>
              <div className="input-group">
                <label className="input-label">{t("Toevoeging")}</label>
                <input
                  className="text-input"
                  type="text"
                  value={s.house_number_addition}
                  onChange={(e) => update("house_number_addition", e.target.value)}
                  placeholder="A"
                />
              </div>
            </div>
            <div className="address-row">
              <button
                type="button"
                className="btn-secondary"
                onClick={lookupAddress}
                disabled={addressLookupLoading}
              >
                {addressLookupLoading ? t("Bezig...") : t("Adres opzoeken")}
              </button>
              {addressLookupError && <span style={{ color: "var(--danger)", fontSize: 13 }}>{addressLookupError}</span>}
              {s.address_street && (
                <span className="address-resolved">
                  {s.address_street}, {s.address_city}
                </span>
              )}
            </div>
            <p className="helper-text" style={{ marginTop: 10 }}>
              {t("Sla over als je geen adresgegevens wilt invullen.")}
            </p>
          </div>
        );

      case "review": {
        const reviewRows: Array<{ label: string; value: string }> = [
          { label: t("Woningtype"), value: s.house_type ? t(s.house_type) : "-" },
          { label: t("Bouwjaar"), value: (() => { const l = BUILD_YEARS.find((b) => b.v === s.build_year)?.l; return l ? t(l) : "-"; })() },
          { label: t("Verwarming nu"), value: s.heating_types.map((h) => t(h)).join(", ") || "-" },
          { label: t("Isolatie"), value: s.insulation_measures.length > 0 ? s.insulation_measures.map((v) => { const l = INSULATION_OPTIONS.find((o) => o.v === v)?.l; return l ? t(l) : v; }).join(", ") : t("Niet opgegeven") },
          { label: t("Verdeling"), value: t(s.distribution_whole_house) },
          { label: t("Dag- / nachttemp."), value: `${s.day_temp || "-"}°C / ${s.night_temp || "-"}°C` },
          { label: t("CV comfortabel op 55°C"), value: s.cv_comfort_55c ? t("Ja") : t("Nee") },
          { label: s.route === "gas" ? t("Gasverbruik") : "BENG1", value: s.route === "gas" ? `${s.yearly_gas_m3 || "-"} m³/jr` : `${s.beng1_value || "-"} kWh/m²` },
        ];
        if (s.postal_code || s.house_number) {
          reviewRows.push({ label: t("Adres"), value: `${s.postal_code || ""} ${s.house_number || ""}${s.house_number_addition ? ` ${s.house_number_addition}` : ""}`.trim() || "-" });
        }
        return (
          <div className="stepper-body">
            <p className="card-subtitle">
              {t("We hebben genoeg gegevens. Controleer hieronder je antwoorden en bekijk je aanbeveling.")}
            </p>
            <div className="review-grid">
              {reviewRows.map((row) => (
                <div key={row.label} className="review-grid-row">
                  <span className="review-grid-label">{row.label}</span>
                  <span className="review-grid-value">{row.value}</span>
                </div>
              ))}
            </div>
            {livePreview && (
              <div className="stepper-preview-block">
                <div className={`stepper-preview-pill verdict-${livePreview.verdict}`}>
                  {livePreview.verdict === "geschikt" && t("Geschikt voor All-Electric")}
                  {livePreview.verdict === "extra_controle" && t("Mogelijk geschikt, extra controle")}
                  {livePreview.verdict === "ongeschikt" && t("Op basis hiervan: niet geschikt")}
                </div>
                <div className="stepper-preview-line">
                  {t("Aanbevolen pomp:")} <strong>{livePreview.pump ?? "-"}</strong>
                </div>
                <div className="stepper-preview-line">
                  {t("Dekking bij -7°C:")} <strong>{Number.isFinite(livePreview.cap) ? `${Math.round(livePreview.cap)}%` : "-"}</strong>
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const isLastStep = safeIdx === totalSteps - 1;

  const submit = () => {
    onSubmit(buildInput);
  };

  return (
    <form
      className="stepper-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (isLastStep) submit();
        else goNext();
      }}
    >
      {/* Progress */}
      <div className="stepper-progress-wrap">
        <div className="stepper-progress-meta">
          <span className="stepper-pill">{t("Stap")} {safeIdx + 1} {t("van")} {totalSteps}</span>
        </div>
        <div className="stepper-progress">
          <div className="stepper-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Step card */}
      <div className="stepper-step" key={current.id} ref={stepCardRef}>
        <h2 className="step-title-xl">{current.title}</h2>
        {current.subtitle && <p className="step-subtext">{current.subtitle}</p>}
        {renderStep()}
      </div>

      {/* Desktop / inline CTAs */}
      <div className="stepper-inline-cta">
        <button
          type="button"
          className="stepper-back-link"
          onClick={goBack}
          disabled={safeIdx === 0}
        >
          ← {t("Terug")}
        </button>
        {isLastStep ? (
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? t("Bezig...") : t("Bekijk aanbeveling")}
          </button>
        ) : (
          <button type="submit" className="btn-primary">
            <span>{t("Volgende")}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        )}
      </div>

      {/* Sticky bottom bar (phones) */}
      <div className="stepper-bottom-cta">
        <button
          type="button"
          className="stepper-back-link"
          onClick={goBack}
          disabled={safeIdx === 0}
        >
          ← {t("Terug")}
        </button>
        {isLastStep ? (
          <button
            type="button"
            className="btn-primary stepper-cta-primary"
            onClick={submit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("Bezig...") : t("Bekijk aanbeveling")}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary stepper-cta-primary"
            onClick={goNext}
          >
            <span>{t("Volgende")}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        )}
      </div>
    </form>
  );
}
